import {useCallback, useState} from 'react'
import {type FailedAPITaskCreationAttempt, getAPITaskParamsFromAttempt} from '../components/TaskCreationFailedBanner'
import type {CreateAPITaskMutationParams} from '../types/api-task'
import {useCreateAPITask, type UseCreateAPITaskOptions} from './use-create-api-task'

/**
 * Wraps `useCreateAPITask` to also track failed creation attempts and produce the
 * props needed to render a `<TaskCreationFailedBannerList>`. Callers that want a
 * banner-driven retry/dismiss UX should use this instead of wiring up the state
 * machine themselves.
 */
export function useCreateAPITaskWithFailureBanner(options: UseCreateAPITaskOptions) {
  const [failedTasks, setFailedTasks] = useState<FailedAPITaskCreationAttempt[]>([])

  const {onError: callerOnError} = options
  const handleError = useCallback(
    (error: Error, variables: CreateAPITaskMutationParams) => {
      setFailedTasks(prev => [
        {...variables, errorMessage: error.message, timestamp: new Date().toISOString()},
        ...prev,
      ])
      callerOnError?.(error, variables)
    },
    [callerOnError],
  )

  const result = useCreateAPITask({...options, onError: handleError})
  const {createTask} = result

  const handleDismiss = useCallback((index: number) => {
    setFailedTasks(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleRetry = useCallback(
    (index: number) => {
      const attempt = failedTasks[index]
      if (!attempt) return
      setFailedTasks(prev => prev.filter((_, i) => i !== index))
      createTask(getAPITaskParamsFromAttempt(attempt))
    },
    [failedTasks, createTask],
  )

  return {
    ...result,
    failureBannerProps: {
      attempts: failedTasks,
      onRetry: handleRetry,
      onDismiss: handleDismiss,
    },
  }
}
